import { UA } from './ua';
import { padStart } from './function';
/**
 * 解析输入 => `Date`
 * @category TimeDate
 * @param value
 * 兼容 iOS 日期格式问题 `value = value.replace(/-/g, '/');`
 */
function parseDate(value) {
    var date;
    if (value instanceof Date) {
        date = value;
    }
    else if (typeof value === 'number') {
        date = new Date(value);
    }
    else if (typeof value === 'string') {
        if (UA.isiOS) {
            // iOS 日期格式兼容
            value = value.replace(/-/g, '/');
        }
        date = new Date(value);
    }
    else {
        date = new Date();
    }
    return date;
}
/**
 * 获取 `Date` 详细信息
 * @category TimeDate
 * @param date
 * @hidden
 */
function getDateData(date) {
    var data = {
        $y: date.getFullYear(),
        $M: date.getMonth(),
        $D: date.getDate(),
        $W: date.getDay(),
        $H: date.getHours(),
        $m: date.getMinutes(),
        $s: date.getSeconds(),
        $ms: date.getMilliseconds(),
    };
    return data;
}
/**
 * 日期格式化正则
 * @category TimeDate
 * @internal
 */
var REGEX_FORMAT = /Y{2,4}|M{1,4}|D{1,2}|h{1,2}|m{1,2}|s{1,2}/g;
/**
 * 格式化日期
 *
 * | 占位符  | 输出   | 备注               |
 * | ------ | ----- | ------------------|
 * | `YY`   | 18    | 两位数的年份         |
 * | `YYYY` | 2018  | 四位数的年份         |
 * | `M`    | 1-12  | 月份，从 1 开始      |
 * | `MM`   | 01-12 | 月份，两位数         |
 * | `D`    | 1-31  | 月份里的一天         |
 * | `DD`   | 01-31 | 月份里的一天，两位数  |
 * | `h`    | 0-23  | 小时               |
 * | `hh`   | 00-23 | 小时，两位数         |
 * | `m`    | 0-59  | 分钟               |
 * | `mm`   | 00-59 | 分钟，两位数         |
 * | `s`    | 0-59  | 秒                 |
 * | `ss`   | 00-59 | 秒 两位数           |
 *
 *
 * ```js
 * FastUtils.formatDate(1591950794000); // => '2020-06-12 16:33:14'
 *
 * // 格式化当前时间
 * FastUtils.formatDate();
 * ```
 *
 * @category TimeDate
 * @param value 日期输入
 * @param formatStr 格式化占位符
 */
function formatDate(value, formatStr) {
    if (value === void 0) { value = new Date(); }
    if (formatStr === void 0) { formatStr = 'YYYY-MM-DD hh:mm:ss'; }
    var date = parseDate(value);
    var dateData = getDateData(date);
    var matches = {
        YY: String(dateData.$y).slice(-2),
        YYYY: dateData.$y,
        M: dateData.$M + 1,
        MM: padStart(dateData.$M + 1, 2, '0'),
        D: dateData.$D,
        DD: padStart(dateData.$D, 2, '0'),
        h: String(dateData.$H),
        hh: padStart(dateData.$H, 2, '0'),
        m: String(dateData.$m),
        mm: padStart(dateData.$m, 2, '0'),
        s: String(dateData.$s),
        ss: padStart(dateData.$s, 2, '0'),
    };
    return formatStr.replace(REGEX_FORMAT, function (match) {
        return matches[match];
    });
}
/**
 * 不同时间单位的单位秒数
 * @category TimeDate
 */
export var timeUnitSecond = {
    minute: 60,
    hour: 60 * 60,
    day: 60 * 60 * 24,
    month: 60 * 60 * 24 * 30,
    year: 60 * 60 * 24 * 365,
};
/**
 * 解析剩余时间
 *
 * ```
 * FastUtils.parseRemainTime({ remainSecond: 100000 }); // => {second:40,minute:46,hour:27,day:0,month:0,year:0}
 * FastUtils.parseRemainTime({ remainSecond: 100000, maxTimeUnit: 'day' }); // => {second:40,minute:46,hour:3,day:1,month:0,year:0}
 * FastUtils.parseRemainTime({ remainSecond: 10000000, maxTimeUnit: 'day' }); // => {second:40,minute:46,hour:17,day:115,month:0,year:0}
 * FastUtils.parseRemainTime({ remainSecond: 10000000, maxTimeUnit: 'month' }); // => {second:40,minute:46,hour:17,day:25,month:3,year:0}
 * FastUtils.parseRemainTime({ remainSecond: 100000000, maxTimeUnit: 'year' }); // => {second:40,minute:46,hour:9,day:2,month:2,year:3}
 * ```
 *
 * @category TimeDate
 * @param options
 * - remainSecond 剩余时间，单位: 秒；
 * - maxTimeUnit 最高解析到哪个时间单位，默认为 `hour`；
 */
function parseRemainTime(options) {
    var remainTime = {
        second: 0,
        minute: 0,
        hour: 0,
        day: 0,
        month: 0,
        year: 0,
    };
    if (!options || !options.remainSecond || options.remainSecond <= 0)
        return remainTime;
    if (typeof options.maxTimeUnit === 'undefined') {
        options.maxTimeUnit = 'hour';
    }
    var maxTimeUnit = options.maxTimeUnit;
    var remainSecond = options.remainSecond;
    var isMatchTimeUnit = false;
    if (isMatchTimeUnit || maxTimeUnit === 'year') {
        var oneYearSecond = timeUnitSecond.year;
        remainTime.year = parseInt(String(remainSecond / oneYearSecond), 10);
        remainSecond = remainSecond - remainTime.year * oneYearSecond;
        isMatchTimeUnit = true;
    }
    if (isMatchTimeUnit || maxTimeUnit === 'month') {
        var oneMonthSecond = timeUnitSecond.month;
        remainTime.month = parseInt(String(remainSecond / oneMonthSecond), 10);
        remainSecond = remainSecond - remainTime.month * oneMonthSecond;
        isMatchTimeUnit = true;
    }
    if (isMatchTimeUnit || maxTimeUnit === 'day') {
        var oneDaySecond = timeUnitSecond.day;
        remainTime.day = parseInt(String(remainSecond / oneDaySecond), 10);
        remainSecond = remainSecond - remainTime.day * oneDaySecond;
        isMatchTimeUnit = true;
    }
    if (isMatchTimeUnit || maxTimeUnit === 'hour') {
        var oneHourSecond = timeUnitSecond.hour;
        remainTime.hour = parseInt(String(remainSecond / oneHourSecond), 10);
        remainSecond = remainSecond - remainTime.hour * oneHourSecond;
        isMatchTimeUnit = true;
    }
    if (isMatchTimeUnit || maxTimeUnit === 'minute') {
        var oneMinuteSecond = timeUnitSecond.minute;
        remainTime.minute = parseInt(String(remainSecond / oneMinuteSecond), 10);
        remainSecond = remainSecond - remainTime.minute * oneMinuteSecond;
        isMatchTimeUnit = true;
    }
    if (isMatchTimeUnit || maxTimeUnit === 'second') {
        remainTime.second = remainSecond;
        isMatchTimeUnit = true;
    }
    return remainTime;
}
/**
 * 倒计时
 *
 * ```js
 * const timer = FastUtils.countDown({
 *   remainSecond: 100,
 *   maxTimeUnit: 'hour',
 *   onUpdate(remainTimeData) {
 *     console.log(remainTimeData);
 *   },
 *   onFinish() {
 *     console.log('finish');
 *   }
 * });
 *
 * // 取消倒计时
 * timer.cancel();
 * ```
 *
 * @category TimeDate
 * @param options
 * - remainSecond 剩余时间，单位: 秒；
 * - maxTimeUnit 最高解析到哪个时间单位，默认为 `hour`；
 * - onUpdate 倒计时更新通知，每秒调用一次，传入 [[IRemainTime]] 信息
 * - onFinish 倒计时结束通知
 * @return 返回 `cancel` 函数，可取消倒计时
 */
function countDown(options) {
    var remainSecond = options.remainSecond;
    var timerId;
    function cancel() {
        if (timerId) {
            clearInterval(timerId);
        }
    }
    // 马上触发第一次更新
    if (typeof options.onUpdate === 'function') {
        options.onUpdate(parseRemainTime({ remainSecond: remainSecond, maxTimeUnit: options.maxTimeUnit }));
    }
    timerId = setInterval(function () {
        remainSecond--;
        if (typeof options.onUpdate === 'function') {
            options.onUpdate(parseRemainTime({ remainSecond: remainSecond, maxTimeUnit: options.maxTimeUnit }));
        }
        if (remainSecond <= 0) {
            typeof options.onFinish === 'function' && options.onFinish();
            cancel();
        }
    }, 1000);
    return {
        cancel: cancel,
    };
}
export { parseDate, formatDate, parseRemainTime, countDown };



//////////////////
// WEBPACK FOOTER
// ./node_modules/@ali/fast-utils/dist/time-date.js
// module id = null
// module chunks = 